import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform } from '@ionic/angular';
// environments
import { environment } from '../../../environments/environment';
// rxjs
import { Observable, Subject, throwError } from 'rxjs';
import {catchError, filter, finalize, map, share, switchMap, tap} from 'rxjs/operators';
// models
import { Customer, CustomerData, CustomerToken, Rewards } from '../models/customer.model';
import {AtriumOrder, CreateSubscriptionData} from '../models/order.model';
// services
import { RouteService } from './route.service';
// stores
import {RestaurantStore} from '../stores/restaurant.store';
import { NO_SELECTED_CARD, STORAGE_CUSTOMER_DATA, STORAGE_TAB_DATA } from '../constants';
import {CartStore} from '../stores/cart.store';
import {StorageService} from './storage.service';
import {PaymentStore, VerifyProcess} from '../stores/payment.store';
import { GroupOrder, GroupOrderTab, OrderTab, PaymentSources, SavedPaymentState } from '../models/payment-item.model';
import {OrderApiService} from './order.api.service';
// constants
import { v4 as uuid4 } from 'uuid';
import { dataToCustomer, dataToCustomerToken } from '../dto/customer.dto';
import { PartialBalance } from '../models/gift-card.model';
import { OneSignalService } from './one-signal.service';
import { dataToGiftCardReload } from '../dto/gift-card.dto';
import { CustomerStore } from '../stores/customer.store';
import { DuplicateCardCheckRequest, DuplicateCardCheckResponse } from '../models/duplicate-card-warning.model';

@Injectable()
export class PaymentService {
  constructor(
    private platform: Platform,
    private http: HttpClient,
    private routeService: RouteService,
    private restaurantStore: RestaurantStore,
    private cartStore: CartStore,
    private storageService: StorageService,
    private orderApiService: OrderApiService,
    private paymentStore: PaymentStore,
    private oneSignalService: OneSignalService,
    private customerStore: CustomerStore
  ) {
  }

  private static readonly httpOptionsForProcessCard = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    })
  };

  public totalPrice: Subject<number> = new Subject<number>();

  public setTotalprice(price:number):void {
    this.totalPrice.next(price);
  }

  public get getTotalPrice(): Observable<number> {
    return this.totalPrice.asObservable();
  }

  public isLoading: Subject<boolean> = new Subject<boolean>();

  public setIsLoading(isLoading:boolean):void {
    this.isLoading.next(isLoading)
  }

  public get getIsLoading(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  public partialBalance: Subject<PartialBalance> = new Subject<PartialBalance>();

  public setPartialBalance(partialBalance: PartialBalance): void {
    this.partialBalance.next(partialBalance);
  }
  public get getPartialBalance(): Observable<PartialBalance> {
    return this.partialBalance.asObservable();
  }

  public getSavedPaymentState(checked: boolean): SavedPaymentState {
    if (!checked) {
      return new SavedPaymentState(NO_SELECTED_CARD, checked);
    }
    return new SavedPaymentState(
      this.paymentStore.savedCardId
        ? this.paymentStore.savedCardId
        : this.paymentStore.customer.cards
          ? this.paymentStore.customer.cards[0].id
          : NO_SELECTED_CARD,
      checked
    );
  }

  public getSavedGiftCardPaymentState(checked: boolean): SavedPaymentState {
    if (!checked) {
      return new SavedPaymentState(NO_SELECTED_CARD, checked);
    }
    return new SavedPaymentState(
        this.paymentStore.customer.giftCards
        ? this.paymentStore.customer.giftCards[0]?.id
        : NO_SELECTED_CARD,
      checked
    );
  }

  public getCustomerByPhone(phoneNumber: string, isSharedCardsEnabled: boolean, isNeedSharingPhoneNumber: boolean): Observable<Customer> {
    const apiVer = isSharedCardsEnabled ? 'v2/' : '';
    const apiURL = !isNeedSharingPhoneNumber ? `${environment.apiUrl}${apiVer}${this.routeService.getBaseUrl()}` : `${environment.apiUrl}${this.routeService.createGroupUrl(this.restaurantStore.group.groupId)}`;

    const body = {
      isMobilePlatform: this.platform.is('mobile')
    };
    const isNeedLoyalty = this.cartStore.isLoyaltyEnabled;
    const idempotencyKey = uuid4();

    return this.http.post(`${apiURL}/customers/phoneNumber/${phoneNumber}?requireLoyalty=${isNeedLoyalty}&idempotencyKey=${idempotencyKey}`, body)
      .pipe(
        map((customer: any) => {
          if (this.platform.is('cordova') && customer.phone_number) {
            this.oneSignalService.addExternalUser(customer.phone_number);
          }
          return dataToCustomer(customer);
        }),
        share()
      );
  }

  public getCustomerByEmail(email: string, token: string, isNeedSharingPhoneNumber: boolean): Observable<{customer:Customer,token:string}> {
    // const apiURL = !isNeedSharingPhoneNumber ? `${environment.apiUrl}${this.routeService.getBaseUrl()}` : `${environment.apiUrl}${this.routeService.createGroupUrl(this.restaurantStore.group.groupId)}`;
    const apiURL =  `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    const body = {
      isMobilePlatform: this.platform.is('mobile'),
      email: email,
      token: token
    };
    const isNeedLoyalty = this.cartStore.isLoyaltyEnabled;
    const idempotencyKey = uuid4();

    return this.http.post(`${apiURL}/customers/email?requireLoyalty=${isNeedLoyalty}&idempotencyKey=${idempotencyKey}`, body)
      .pipe(
        map((response: any) => {
          if (this.platform.is('cordova') && response.customer.phone_number) {
            this.oneSignalService.addExternalUser(response.customer.phone_number);
          }
          const customer = dataToCustomer(response.customer)
          return {customer,token:response.token};
        })
      );
  }

  public getVerifiedCustomerByPhone(phoneNumber: string, token: string, isSharedCardsEnabled: boolean, isNeedSharingPhoneNumber: boolean, merchantId?: string): Observable<Customer> {
    const apiVer = isSharedCardsEnabled ? 'v2/' : '';
    let apiURL = !isNeedSharingPhoneNumber ? `${environment.apiUrl}${apiVer}${this.routeService.getBaseUrl()}` : `${environment.apiUrl}${this.routeService.createGroupUrl(this.restaurantStore.group.groupId)}`;
    if (merchantId) {
      apiURL = `${environment.apiUrl}${apiVer}merchants/${merchantId}`
    }
    const isNeedLoyalty = this.cartStore.isLoyaltyEnabled;
    const idempotencyKey = uuid4();

    return this.http.post(`${apiURL}/customers/phoneNumber/${phoneNumber}/authorize?requireLoyalty=${isNeedLoyalty}&idempotencyKey=${idempotencyKey}`, token)
      .pipe(
        map((customer: any) => {
          if (this.platform.is('cordova') && customer.phone_number) {
            this.oneSignalService.addExternalUser(customer.phone_number);
          }
          return dataToCustomer(customer);
        }),
        share()
      );
  }

  public getCustomerById(customerId: string, isShare:boolean = true): Observable<Customer> {
    const apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    return this.http.get(`${apiURL}/customers/${customerId}?share=${isShare}`).pipe(
      map((customer: any) => dataToCustomer(customer)),
      share()
    );
  }

  public getTransactionById(transactionId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}transaction/${transactionId}`);
  }

  public updateCustomerPlatform(customerId: string): Observable<any> {
    const apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    const body = { isMobilePlatform: this.platform.is('mobile') };

    return this.http.post(`${apiURL}customers/${customerId}`, body);
  }

  public updateCustomerById(givenName:string,familyName:string,email:string,orderBotUserId:string):Observable<any> {
    const apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    const body = {given_name:givenName,family_name:familyName,email_address:email}
    return this.http.post(`${environment.apiUrl}customers/${orderBotUserId}`,body)
  }

  public removeCustomerCard(customerId: string, cardId: string, sharedCards: boolean, isSharedCardsEnabled: boolean): Observable<Object> {
    const apiVer = isSharedCardsEnabled ? 'v2/' : '';
    const shared = isSharedCardsEnabled ? `?shared_cards=${sharedCards}` : '';
    const apiURL = `${environment.apiUrl}${apiVer}${this.routeService.getBaseUrl()}`;
    return this.http.delete(`${apiURL}/customers/${customerId}/cards/${cardId}${shared}`);
  }

  public payWithSavedCard(transactionId: string, cardId: string, isGiftCard = false): Observable<Object> {
    const addGiftCardParam = isGiftCard ? '?isGiftCard=true' : '';
    return this.http.post(`${environment.apiUrl}transaction/${transactionId}/card/${cardId}${addGiftCardParam}`, {});
  }

  public groupSaveCard(nonce: string, orderId: string, givenName: string, familyName: string): Observable<Object> {
    const sendData = `nonce=${nonce}&orderId=${orderId}&givenName=${givenName}&familyName=${familyName}`;
    return this.http.post(`${environment.apiUrl}api/group_orders/save_card`, sendData, PaymentService.httpOptionsForProcessCard);
  }

  public groupProcessOrder(nonce: string, orderId: string, givenName: string, familyName: string): Observable<Object> {
    const sendData = `nonce=${nonce}&orderId=${orderId}&givenName=${givenName}&familyName=${familyName}`;
    return this.http.post(`${environment.apiUrl}api/group_orders/process_order`, sendData, PaymentService.httpOptionsForProcessCard);
  }

  public groupPayWithSavedCard(groupOrderId: string, cardId: string): Observable<Object> {
    return this.http.post(`${environment.apiUrl}group_orders/${groupOrderId}/card/${cardId}`, {});
  }

  public getVerifyingCode(customerId: string): Observable<Object> {
    return this.http.post(`${environment.apiUrl}customers/${customerId}/twofactor/reset`, {});
  }

  public sendVerifyingCode(customerId: string, code: any): Observable<CustomerToken> {
    const sentData = { entered_pin: code };

    return this.http.post(`${environment.apiUrl}customers/${customerId}/twofactor/verify`, sentData)
      .pipe(
        map((customerToken: any) => {
          return dataToCustomerToken(customerToken);
        })
      );
  }

  public saveCard(
    nonce: string,
    transactionId: string,
    givenName?: string,
    familyName?: string,
    isGiftCard?: boolean,
    buyer_email?: string
  ): Observable<Object> {

    const parts: string[] = [
      `nonce=${encodeURIComponent(nonce)}`,
      `transactionId=${encodeURIComponent(transactionId)}`
    ];

    if (givenName) {
      parts.push(`givenName=${encodeURIComponent(givenName)}`);
    }

    if (familyName) {
      parts.push(`familyName=${encodeURIComponent(familyName)}`);
    }

    if (typeof isGiftCard === 'boolean') {
      parts.push(`isGiftCard=${encodeURIComponent(String(isGiftCard))}`);
    }

    if (buyer_email) {
      parts.push(`buyer_email=${encodeURIComponent(buyer_email)}`);
    }

    const sendData = parts.join('&');

    return this.http.post(
      environment.apiUrl + 'api/save_card',
      sendData,
      PaymentService.httpOptionsForProcessCard
    );
  }


  public saveCustomerCard(nonce: string, givenName: string, familyName: string, customerId: string, share = true): Observable<string> {
    const body = {
      sourceId: nonce,
      givenName: givenName,
      familyName: familyName,
      share
    };
    return this.http.post<string>(environment.apiUrl + `customers/${customerId}/cards`, body, { responseType: 'text' as 'json' });
  }

  public processOrder(nonce: string, transactionId: string, isGiftCard: boolean): Observable<Object> {
    const sendData = `nonce=${nonce}&transactionId=${transactionId}&isGiftCard=${isGiftCard}`;
    return this.http.post(environment.apiUrl + 'api/process_order', sendData, PaymentService.httpOptionsForProcessCard);
  }
  public paymentWithSeveralSources(transactionId: string, sources: PaymentSources): Observable<any> {
    return this.http.post(environment.apiUrl + 'transactions/' + transactionId + '/pay', sources);
  }

  public atriumPayment(atriumOrder: AtriumOrder): Observable<any> {
    return this.http.post(`${environment.apiUrl}api/atrium/process_order`, atriumOrder);
  }

  public async getCustomerOpenedTab(): Promise<void> {
    if ((this.cartStore.groupTab || this.cartStore.tab) && !this.paymentStore.customer) {
      await this.initCustomer();
    } else if (await this.storageService.get(STORAGE_CUSTOMER_DATA)) {
      await this.checkCustomerOpenTab();
    }
  }

  public async initCustomer(): Promise<void> {
    if (this.cartStore.groupTab || this.cartStore.tab) {
      let customerId: string;
      if (this.cartStore.groupTab) {
        this.setFeeForGroupTabs();
        customerId = this.cartStore.groupTab.customer_id;
      } else if (this.cartStore.tab) {
        customerId = this.cartStore.tab.customer_id;
      }
      this.getCustomerById(customerId)
        .subscribe((customer: Customer) => {
          this.paymentStore.customer = customer;
          this.cartStore.showTabModal = true;
          this.saveCustomerToStorage();
        });
    }
  }

  public async checkCustomerOpenTab(): Promise<void> {
    if (await this.storageService.get(STORAGE_TAB_DATA)) {
      const { tabId, locationId, isGroup } = await this.storageService.get(STORAGE_TAB_DATA);
      if (isGroup) {
        this.orderApiService.getGroupTabById(tabId)
          .pipe(filter(Boolean))
          .subscribe((tab: GroupOrderTab) => {
            this.cartStore.groupTab = tab;
            this.setFeeForGroupTabs();
            if (!tab.is_closed) {
              if (!this.paymentStore.customer) {
                this.initCustomer();
              }
            } else if (tab.is_closed) {
              this.removeCustomerFromStorage();
            }
          });
      } else {
        this.orderApiService.getTabById(tabId)
          .pipe(filter(Boolean))
          .subscribe((tab: OrderTab) => {
            this.cartStore.tab = tab;
            if (tab.location_id === locationId && !tab.is_closed) {
              if (!this.paymentStore.customer) {
                this.initCustomer();
              }
            } else if (tab.is_closed) {
              this.removeCustomerFromStorage();
            }
          });
      }
    }
  }

  public setFeeForGroupTabs(): void {
    this.cartStore.kyooFee = this.restaurantStore.group.tabsKyooFee;
    this.cartStore.additionalKyooFee = this.restaurantStore.group.tabsAdditionalKyooFee;
  }

  private async saveCustomerToStorage(): Promise<void> {
    const storedCustomerData = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    await this.storageService.set(
      STORAGE_CUSTOMER_DATA,
      new CustomerData(
        storedCustomerData?.token ?? null,
        this.paymentStore.customer.phone,
        this.paymentStore.customer.orderBotUserId
      )
    );
    if (this.cartStore.groupTab && this.cartStore.groupTab.is_closed) {
      this.storageService.remove(STORAGE_TAB_DATA);
    }
    if (this.cartStore.tab && this.cartStore.tab.is_closed) {
      this.storageService.remove(STORAGE_TAB_DATA);
    }
  }

  private removeCustomerFromStorage(): void {
    this.cartStore.showTabModal = true;
    this.storageService.remove(STORAGE_TAB_DATA);
  }

  public removeAccount(merchantId: string, squareCustomerId: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}merchants/${merchantId}/customers/${squareCustomerId}`)
  }

  public getCustomerRewardPoints(customerReferenceId: string): any {
    const apiURL = environment.apiUrl;
    return this.http.get<Rewards>(`${apiURL}customers/${customerReferenceId}/rewards`)
    .pipe(
      catchError((error: any) => {
        console.error('Error fetching rewards:', error);
        return throwError(error);
      })
    );
  }

  public getLocationsByReward(rewardId: string): any {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${this.routeService.getMerchantId()}/loyalty/rewards/${rewardId}/locations`);
  }

  public removeGiftCardFromCustomer(customerId: string, merchantId: string, giftCardId:string) {
    const apiURL = environment.apiUrl;
    const payload = {
      merchant_id: merchantId,
      nonce: giftCardId
    };
    return this.http.post(`${apiURL}gift-cards/${customerId}/remove-card`, payload);
  }

  public addGiftCardToCustomer(customerId: string, merchantId: string, gan: string) {
    const apiURL = environment.apiUrl;
    const payload = {
      merchant_id: merchantId,
      nonce: gan
    };
    return this.http.post(`${apiURL}gift-cards/${customerId}/save-card`, payload);
  }

  public getCustomerGiftCards(orderBotUserId: string, token: string): Observable<any> {
    const apiURL = `${environment.apiUrl}`;
    return this.http.post(`${apiURL}customers/${orderBotUserId}/gift-cards`,token)
      .pipe(
        map((data: any) =>
          data.map(item => dataToGiftCardReload(item))
        ),
        share()
      );
  }

  public loadAndVerifyCustomerFlow(
    phone: string,
    token: string,
    isSharedCardsEnabled: boolean,
    isNeedSharingPhoneNumber: boolean,
    merchantId?: string
  ): Observable<Customer> {
    this.customerStore.setCustomerLoading(true);
    this.paymentStore.verifyCustomerState = VerifyProcess.VERIFYING;
    this.paymentStore.setPhoneNumber(phone);

    return this
      .getVerifiedCustomerByPhone(phone, token, isSharedCardsEnabled, isNeedSharingPhoneNumber, merchantId)
      .pipe(
        tap((customer) => {
          this.paymentStore.customer = customer;
          this.paymentStore.saveCustomerInfoToStore();
          this.paymentStore.verificationCustomer = true;
          this.paymentStore.verifyCustomerState = VerifyProcess.SUCCESS;
        }),
        switchMap(() => this.getCustomerGiftCards(this.paymentStore.customer.orderBotUserId, token)),
        tap((giftCards) => {
          this.paymentStore.setGiftCardReload(giftCards);
          const ganList = giftCards.map(card => card.gan).filter(Boolean);
          if (ganList.length) {
            this.customerStore.loadGiftCardOrders(ganList);
          }
        }),
        finalize(() => {
          this.customerStore.setCustomerLoading(false);
        })
      );
  }

  public checkDuplicateCard(
    transactionId: string,
    card: DuplicateCardCheckRequest
  ): Observable<DuplicateCardCheckResponse> {
    return this.http.post<DuplicateCardCheckResponse>(
      `${environment.apiUrl}transactions/${encodeURIComponent(transactionId)}/duplicate-card-check`,
      card
    );
  }

  public confirmDuplicateCardWarning(
    transactionId: string,
    warningId: string
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}transactions/${encodeURIComponent(transactionId)}/duplicate-card-warnings/${encodeURIComponent(warningId)}/confirm`,
      {}
    );
  }

  public cancelDuplicateCardWarning(
    transactionId: string,
    warningId: string
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}transactions/${encodeURIComponent(transactionId)}/duplicate-card-warnings/${encodeURIComponent(warningId)}/cancel`,
      {}
    );
  }

}
